import styled from "@emotion/native"
import { Spacing, Text, ButtonV2, FullScreenBlur } from "../../ui-components"
import { LinkExit } from "react-native-plaid-link-sdk"
import { FinishKycResponse, useFinishKyc, useStartKyc } from "../../api/mutations"
import { useEffect, useState } from "react"
import { PlaidLink } from "../Plaid/PlaidLink"
import { useStateWithCallback } from "../../hooks"
import { Sentry } from "../../utils/Sentry"
import { usePushNotifications } from "../../hooks/usePushNotifications"
import { OnboardingScreenWrapper } from "./OnboardingScreenWrapper"

interface VerifyIdentityV2UIProps {
  onFinally: (response: FinishKycResponse) => void
  onSkip: () => void
  hideSkippingAccount?: boolean
  onCancel: () => void
  onSuccess?: () => Promise<unknown>
}

export function VerifyIdentityV2UI({
  onFinally,
  onSkip,
  hideSkippingAccount,
  onCancel,
  onSuccess,
}: VerifyIdentityV2UIProps) {
  const { mutateAsync: startKyc, isPending: isKycPending } = useStartKyc()
  const finishKyc = useFinishKyc()
  const [kycTokenId, setKycTokenId] = useState<string>()
  const [identityVerificationToken, setIdentityVerificationToken] = useState<string>()
  const [linkSessionId, setLinkSessionId] = useState<string>()
  const [linkFinished, setLinkFinished] = useStateWithCallback<boolean>(false)
  const [tryAgainLoading, setTryAgainLoading] = useState(true)
  const { addToken } = usePushNotifications()

  const handleIdentityVerificationSuccess = async (linkSessionId: string) => {
    if (!kycTokenId) {
      return
    }
    const response = await finishKyc.mutateAsync({
      identityVerificationExternalId: linkSessionId,
      tokenId: kycTokenId,
    })
    await addToken()
    if (onSuccess) {
      await onSuccess()
    }
    setLinkSessionId(linkSessionId)
    onFinally(response)
  }

  const handleIdentityVerificationFailure = async (
    linkSessionId: string,
    wasCanceled?: boolean
  ) => {
    if (wasCanceled) {
      onCancel()
      return
    }
    if (!kycTokenId) {
      return
    }
    const response = await finishKyc.mutateAsync({
      identityVerificationExternalId: linkSessionId,
      tokenId: kycTokenId,
    })
    await addToken()
    setLinkSessionId(linkSessionId)
    Sentry.captureMessage("errors in FinishKyc", {
      extra: { isOk: response.isOk, errors: response.errors },
    })

    onFinally(response)
  }

  function renderBlurView() {
    if (isKycPending) {
      return <FullScreenBlur loadingText="Loading identity verification..." />
    }

    return null
  }

  useEffect(() => {
    if (linkSessionId) {
      return
    }
    const startPlaidFlowAsync = async () => {
      const startKycResponse = await startKyc()
      setKycTokenId(startKycResponse.tokenId)
      setIdentityVerificationToken(startKycResponse.linkToken)
    }
    startPlaidFlowAsync()
  }, [linkSessionId, startKyc])

  async function plaidCallback(linkSessionId: string) {
    setLinkFinished(true, () => {
      handleIdentityVerificationSuccess(linkSessionId).finally(() => setTryAgainLoading(false))
    })
  }

  async function plaidFailedCallback(linkSessionId: string, wasCanceled?: boolean) {
    setLinkFinished(true, () => {
      handleIdentityVerificationFailure(linkSessionId, wasCanceled).finally(() =>
        setTryAgainLoading(false)
      )
    })
  }

  return (
    <OnboardingScreenWrapper stepNumber={5}>
      <Container>
        <UpperTextsWrapper>
          <Text type="h1">Verify your identity</Text>
          <Spacing top={8} />
          <Text type="body2" color="secondary">
            SynchPay uses Plaid to verify your identity
          </Text>
        </UpperTextsWrapper>
        <Spacing top={20} />
        {linkFinished && (
          <>
            <ProgressMessage>
              <Text type="body2" color="secondary">
                We are confirming your identity. Please wait..
              </Text>
            </ProgressMessage>
            <ButtonsWrapper>
              <ButtonV2
                title="Try again"
                variant="blue2"
                onPress={() => {
                  setLinkFinished(false, () => null)
                  setTryAgainLoading(true)
                }}
                loading={finishKyc.isPending || tryAgainLoading}
              />
              {hideSkippingAccount ? null : (
                <ButtonV2
                  title="Skip"
                  variant="zumthor"
                  onPress={onSkip}
                  loading={finishKyc.isPending || tryAgainLoading}
                />
              )}
            </ButtonsWrapper>
          </>
        )}
        {identityVerificationToken && !linkFinished && (
          <PlaidLink
            linkToken={identityVerificationToken}
            onSuccess={(success) => {
              plaidCallback(success.metadata.linkSessionId)
            }}
            onExit={(response: LinkExit) => {
              const wasCanceled =
                !response.error || Object.values(response.error).every((field) => !field)

              plaidFailedCallback(response.metadata.linkSessionId, wasCanceled)
            }}
            launchOnMount
          >
            <ButtonsWrapper>
              <ButtonV2 title="Try again" loading={tryAgainLoading} variant="blue2" />
              {hideSkippingAccount ? null : (
                <ButtonV2
                  title="Skip"
                  variant="zumthor"
                  onPress={onSkip}
                  loading={tryAgainLoading}
                />
              )}
            </ButtonsWrapper>
          </PlaidLink>
        )}
      </Container>
      {renderBlurView()}
    </OnboardingScreenWrapper>
  )
}

const Container = styled.View`
  flex: 1;
  justify-content: space-between;
  padding: 20px;
  max-width: 500px;
  width: 100%;
  align-self: center;
`

const ButtonsWrapper = styled.View`
  gap: 16px;
`

const UpperTextsWrapper = styled.View``

const ProgressMessage = styled.View`
  align-items: center;
`
